import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

interface SourceCounts {
  companies: number
  contacts: number
  invoices: number
  tickets: number
  quotes: number
  services: number
  total: number
}

export interface SourceItem {
  uuid: string
  name: string
  modelConnector: string
  active: boolean
  createdAt: string
  updatedAt: string
  counts: SourceCounts
}

export interface SourceInput {
  name: string
  modelConnector: string
  active?: boolean
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

export const useSourceAdminStore = defineStore('sourceAdmin', () => {
  const suiteStore = useSuiteStore()
  const sources = ref<SourceItem[]>([])
  const isLoading = ref(false)

  async function fetchAll(): Promise<SourceItem[]> {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch('sources/with-counts', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = (await res.json()) as SourceItem[]
        sources.value = (json ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))
        return sources.value
      }
    } catch (e) {
      console.error('Erreur récupération sources', e)
    } finally {
      isLoading.value = false
    }
    return []
  }

  async function create(input: SourceInput): Promise<StoreResult<SourceItem>> {
    const res = await suiteStore.suiteFetch('sources', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as SourceItem
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la création de la source')
      : 'Erreur lors de la création de la source'
    return { ok: false, message }
  }

  async function update(uuid: string, input: Partial<SourceInput>): Promise<StoreResult<SourceItem>> {
    const res = await suiteStore.suiteFetch(`sources/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as SourceItem
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  async function remove(uuid: string): Promise<StoreResult<true>> {
    const res = await suiteStore.suiteFetch(`sources/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      sources.value = sources.value.filter((s) => s.uuid !== uuid)
      return { ok: true, data: true }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Suppression impossible')
      : 'Suppression impossible'
    return { ok: false, message }
  }

  return { sources, isLoading, fetchAll, create, update, remove }
})
